import { DEFAULT_HANDLING, type Handling } from './Physics';

export const PHYSICS_VERSION = '0.2.19';

const COLLISION_RESPONSE_DEFAULTS: Pick<
  Handling,
  | 'collisionRestitution'
  | 'collisionSlideFriction'
  | 'collisionContactFriction'
  | 'collisionPersistentSeconds'
  | 'collisionMinBounceSpeed'
  | 'collisionSeparationSpeed'
  | 'collisionSameNormalDot'
  | 'collisionEngineKick'
  | 'collisionEngineSqueeze'
  | 'collisionCockpitPunch'
  | 'collisionContactFrictionRecovery'
  | 'collisionHitstopMaxMs'
  | 'collisionHitstopSpeedRef'
  | 'collisionFovPunchDeg'
  | 'collisionMaxFrameLoss'
> = {
  collisionRestitution: 0.12,
  collisionSlideFriction: 0.08,
  collisionContactFriction: 0.75,
  collisionPersistentSeconds: 0.05,
  collisionMinBounceSpeed: 7,
  collisionSeparationSpeed: 0.35,
  collisionSameNormalDot: 0.86,
  collisionEngineKick: 3.2,
  collisionEngineSqueeze: 1.9,
  collisionCockpitPunch: 0.16,
  collisionContactFrictionRecovery: 0.4,
  collisionHitstopMaxMs: 70,
  collisionHitstopSpeedRef: 55,
  collisionFovPunchDeg: 3.0,
  collisionMaxFrameLoss: 30,
};

export const PHYSICS_VERSIONS: Record<string, Handling> = {
  // 0.2.18 restores the easier boost-charge tune (driftChargeRate 0.25 → 0.45,
  // driftChargeYawSat 1.5 → 0.5). The ghost-replay table mirrors the live
  // defaults in Handling.defaults.json so newly recorded ghosts replay
  // deterministically against the same physics that produced them.
  '0.2.18': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 37,
    drag: 0.15,
    grip: 7.5,
    turnRate: 2.5,
    offTrackDrag: 1.0,
    offTrackGrip: 1.5,
    reverseSpeed: 18,
    brakeGripMultiplier: 2.0,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.4,
    lowSpeedSteerMultiplier: 0.57,
    highSpeedSteerMultiplier: 0.48,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.5,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.63,
    rotationalDrag: 4.0,
    weatherVaneStrength: 4.9,
    turnInGripStrength: 0.3,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 4.0,
    idleStopSoftSpeedCap: 5.0,
    idleStopHardSnapSpeed: 0.3,
    brakeLowSpeedTaperRange: 4.0,
    airBrakeDragRate: 1.0,
    // Boost charge tune: hard cornering produces ~0.13 rad slip with the
    // current grip setup, so the legacy 0.15 enter threshold gated charging
    // out of normal play entirely. Drop entry to 0.02 so any committed
    // corner registers as drift; rate of 0.4 targets ~1.5 meter fills per
    // lap (a single corner adds ~5%, a committed brake-drift ~10%).
    driftChargeRate: 0.4,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 0.5,
    driftSlipEnterThreshold: 0.02,
    driftSlipExitThreshold: 0.01,
    driftBoostMinCharge: 0,
    driftBoostThrust: 25,
    driftBoostMaxDuration: 1.5,
    driftBoostMaxSpeedMultiplier: 1.10,
    boostConsumeRate: 0.35,
  },
  // 0.2.17 is the Handling rename of the dialed-in saved defaults.
  '0.2.17': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 37,
    drag: 0.15,
    grip: 7.5,
    turnRate: 2.5,
    offTrackDrag: 1.0,
    offTrackGrip: 1.5,
    reverseSpeed: 18,
    brakeGripMultiplier: 2.0,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.4,
    lowSpeedSteerMultiplier: 0.57,
    highSpeedSteerMultiplier: 0.48,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.5,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.63,
    rotationalDrag: 4.0,
    weatherVaneStrength: 4.9,
    turnInGripStrength: 0.3,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 4.0,
    idleStopSoftSpeedCap: 5.0,
    idleStopHardSnapSpeed: 0.3,
    brakeLowSpeedTaperRange: 4.0,
    airBrakeDragRate: 1.0,
    driftChargeRate: 0.25,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0,
    driftBoostThrust: 25,
    driftBoostMaxDuration: 1.5,
    driftBoostMaxSpeedMultiplier: 1.10,
    boostConsumeRate: 0.35,
  },
  '0.2.16': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 60,
    // Hover-craft physics with grounded grip. Baseline grip is non-trivial
    // (the hover field provides real lateral coupling — driving on glass
    // is no fun), turn-in carving adds extra grip while yawing for the
    // Wipeout "scrape into the corner" feel, idle stop snaps the pod to
    // rest when the player has fully let go, and brake tapers near zero
    // so it doesn't push the body backwards from frame quantization.
    drag: 0.15,
    grip: 3.0,
    turnRate: 3.15,
    offTrackDrag: 1.0,
    offTrackGrip: 1.5,
    reverseSpeed: 18,
    brakeGripMultiplier: 2.0,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.0,
    lowSpeedSteerMultiplier: 0.58,
    highSpeedSteerMultiplier: 0.55,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.5,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.2,
    rotationalDrag: 4.0,
    weatherVaneStrength: 2.5,
    // Wipeout / F-Zero pattern: drift is the natural state during fast
    // turns, not a triggered mode. Low baseline grip + weak weather-vane
    // = body slides during normal cornering. Brake-tap and differential
    // are now for *committing* to a deeper slide, not the only entry.
    turnInGripStrength: 0,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 4.0,
    idleStopSoftSpeedCap: 5.0,
    idleStopHardSnapSpeed: 0.3,
    brakeLowSpeedTaperRange: 4.0,
    airBrakeDragRate: 1.0,
    // Drift boost: charge accrues during slip (slow enough that it's
    // earned), persists indefinitely (no passive bleed — banks until
    // used), no minimum-charge threshold (Enter fires whatever you've
    // got, big or small). Burst duration scales with charge magnitude.
    // Charge speed scales with slip × speed × yaw-rate, so the harder
    // you're cornering the faster the meter fills.
    driftChargeRate: 0.25,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0,
    // Hold-to-boost: charge drains at boostConsumeRate while Enter is
    // held. A full meter (1.0) at 0.35/sec drain = ~2.85s of constant
    // boost. Output is constant: +25 m/s² thrust and a +10% speed cap.
    driftBoostThrust: 25,
    driftBoostMaxDuration: 1.5,
    driftBoostMaxSpeedMultiplier: 1.10,
    boostConsumeRate: 0.35,
  },
  '0.2.15': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 37,
    drag: 0.15,
    grip: 7.5,
    turnRate: 2.5,
    offTrackDrag: 1.0,
    offTrackGrip: 1.5,
    reverseSpeed: 18,
    brakeGripMultiplier: 2.0,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.4,
    lowSpeedSteerMultiplier: 0.57,
    highSpeedSteerMultiplier: 0.48,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.5,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.63,
    rotationalDrag: 4.0,
    weatherVaneStrength: 4.9,
    turnInGripStrength: 0.3,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 4.0,
    idleStopSoftSpeedCap: 5.0,
    idleStopHardSnapSpeed: 0.3,
    brakeLowSpeedTaperRange: 4.0,
    airBrakeDragRate: 1.0,
    driftChargeRate: 0.25,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0,
    driftBoostThrust: 25,
    driftBoostMaxDuration: 1.5,
    driftBoostMaxSpeedMultiplier: 1.10,
    boostConsumeRate: 0.35,
  },
  '0.2.14': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 60,
    // Hover-craft physics with grounded grip. Baseline grip is non-trivial
    // (the hover field provides real lateral coupling — driving on glass
    // is no fun), turn-in carving adds extra grip while yawing for the
    // Wipeout "scrape into the corner" feel, idle stop snaps the pod to
    // rest when the player has fully let go, and brake tapers near zero
    // so it doesn't push the body backwards from frame quantization.
    drag: 0.15,
    grip: 3.0,
    turnRate: 3.15,
    offTrackDrag: 1.0,
    offTrackGrip: 1.5,
    reverseSpeed: 18,
    brakeGripMultiplier: 2.0,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.0,
    lowSpeedSteerMultiplier: 0.58,
    highSpeedSteerMultiplier: 0.55,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.5,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.2,
    rotationalDrag: 4.0,
    weatherVaneStrength: 2.5,
    // Wipeout / F-Zero pattern: drift is the natural state during fast
    // turns, not a triggered mode. Low baseline grip + weak weather-vane
    // = body slides during normal cornering. Brake-tap and differential
    // are now for *committing* to a deeper slide, not the only entry.
    turnInGripStrength: 0,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 4.0,
    idleStopSoftSpeedCap: 5.0,
    idleStopHardSnapSpeed: 0.3,
    brakeLowSpeedTaperRange: 4.0,
    airBrakeDragRate: 1.0,
    // Drift boost: charge accrues during slip (slow enough that it's
    // earned), persists indefinitely (no passive bleed — banks until
    // used), no minimum-charge threshold (Enter fires whatever you've
    // got, big or small). Burst duration scales with charge magnitude.
    // Charge speed scales with slip × speed × yaw-rate, so the harder
    // you're cornering the faster the meter fills.
    driftChargeRate: 0.25,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0,
    // Hold-to-boost: charge drains at boostConsumeRate while Enter is
    // held. A full meter (1.0) at 0.35/sec drain = ~2.85s of constant
    // boost. Output is constant: +65 m/s² thrust, speed cap raised by
    // ~8% (86 vs 80) — F1-DRS-style overtake aid, not a win button.
    // Going slow + boost = fast acceleration recovery; already at top
    // speed + boost = brief sustained peak slightly over baseline cap.
    driftBoostThrust: 25,
    driftBoostMaxDuration: 1.5,
    driftBoostMaxSpeedMultiplier: 1.10,
    boostConsumeRate: 0.35,
  },
  '0.2.13': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 60,
    drag: 0.4,
    grip: 8.0,
    turnRate: 3.0,
    offTrackDrag: 4.0,
    offTrackGrip: 4.0,
    reverseSpeed: 18,
    brakeGripMultiplier: 1.6,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.0,
    lowSpeedSteerMultiplier: 0.5,
    highSpeedSteerMultiplier: 0.5,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.7,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.32,
    // Pre-0.2.14 physics: high rotational drag mimics the snap-to-target
    // LPF behavior; no weather-vane (alignment came from grip, not aero).
    rotationalDrag: 30,
    weatherVaneStrength: 0,
    // Turn-in grip / idle stop / brake taper did not exist pre-0.2.14;
    // disable them to preserve the original behavior in legacy replays.
    turnInGripStrength: 0,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 0,
    idleStopSoftSpeedCap: 0,
    idleStopHardSnapSpeed: 0,
    brakeLowSpeedTaperRange: 0,
    airBrakeDragRate: 0,
    // Drift boost did not exist in pre-0.2.14 physics — disable it
    // for historical ghost / replay consistency.
    driftChargeRate: 0,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0.33,
    driftBoostThrust: 0,
    driftBoostMaxDuration: 0,
    driftBoostMaxSpeedMultiplier: 1,
    boostConsumeRate: 0,
  },
  '0.2.12': {
    ...COLLISION_RESPONSE_DEFAULTS,
    maxSpeed: 80,
    accel: 40,
    brakeStrength: 60,
    drag: 0.4,
    grip: 8.0,
    turnRate: 3.0,
    offTrackDrag: 4.0,
    offTrackGrip: 4.0,
    reverseSpeed: 18,
    brakeGripMultiplier: 1.6,
    brakeTurnMultiplier: 1.25,
    steerCurve: 1.0,
    lowSpeedSteerMultiplier: 0.5,
    highSpeedSteerMultiplier: 0.5,
    differentialYawRate: 2.1,
    differentialYawSpeedAttenuation: 0.7,
    differentialReverseRatio: 0.3,
    driftGripFactor: 0.32,
    // Pre-0.2.14 physics: high rotational drag mimics the snap-to-target
    // LPF behavior; no weather-vane (alignment came from grip, not aero).
    rotationalDrag: 30,
    weatherVaneStrength: 0,
    // Turn-in grip / idle stop / brake taper did not exist pre-0.2.14;
    // disable them to preserve the original behavior in legacy replays.
    turnInGripStrength: 0,
    turnInGripYawThreshold: 1.5,
    idleStopSoftRate: 0,
    idleStopSoftSpeedCap: 0,
    idleStopHardSnapSpeed: 0,
    brakeLowSpeedTaperRange: 0,
    airBrakeDragRate: 0,
    // Drift boost did not exist in pre-0.2.14 physics — disable it
    // for historical ghost / replay consistency.
    driftChargeRate: 0,
    driftChargeDecayRate: 0,
    driftChargeYawSat: 1.5,
    driftSlipEnterThreshold: 0.15,
    driftSlipExitThreshold: 0.08,
    driftBoostMinCharge: 0.33,
    driftBoostThrust: 0,
    driftBoostMaxDuration: 0,
    driftBoostMaxSpeedMultiplier: 1,
    boostConsumeRate: 0,
  },
};

// 0.2.19 keeps 0.2.18 handling but moves the universal fixed-step clock to
// 120Hz. Use a new stamp so saved/server ghosts recorded at 60Hz do not play
// back one sample per 120Hz step.
PHYSICS_VERSIONS['0.2.19'] = { ...PHYSICS_VERSIONS['0.2.18'] };

// The current handling values live in Handling.defaults.json (loaded as
// DEFAULT_HANDLING). PHYSICS_VERSIONS keeps historical entries for ghost
// replay determinism — old ghosts are stamped with PHYSICS_VERSION and
// replayed via PHYSICS_VERSIONS[recordedVersion]. The "live" version is
// not read from the table any more, so save-to-code on Handling defaults
// takes effect on the next reload without hand-editing this file.
export function activeHandling(): Handling {
  return { ...DEFAULT_HANDLING };
}

/** @deprecated Use activeHandling. Kept for one release while call sites migrate. */
export const activePhysics = activeHandling;
